import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { operationApi } from '../../services/api';
import { UserPlus, Edit, Trash2, Heart, IdCard, QrCode, Printer, Percent, X, Tag, Link2, Copy, PackagePlus, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://klinnovac.id';

const STATUS_OPTIONS = ['active', 'inactive'];
const GENDER_OPTIONS = ['Laki-laki', 'Perempuan'];
const BLOOD_OPTIONS = ['A', 'B', 'AB', 'O'];
const MAX_FILE_SIZE = 15 * 1024 * 1024;

export default function OperationWorkersPage() {
  const [profiles, setProfiles] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [form, setForm] = useState({
    email: '', password: 'password123', employee_id: '', full_name: '', phone: '', address: '',
    date_of_birth: '', gender: '', position: '', department: '', hire_date: '', contract_end_date: '',
    emergency_contact: '', emergency_phone: '', blood_type: '', notes: '', status: 'active',
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [ktpPreview, setKtpPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [ktpFile, setKtpFile] = useState(null);

  const [pricingData, setPricingData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);

  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignedItemIds, setAssignedItemIds] = useState([]);
  const [assignedPackageIds, setAssignedPackageIds] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState([]);

  const [positionsList, setPositionsList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [activeTab, setActiveTab] = useState('idcard');
  const [downloadingCard, setDownloadingCard] = useState(false);

  const cardRef = useRef(null);

  const getErrorMessage = (err, fallback = 'Gagal menyimpan') => {
    const data = err?.response?.data;
    if (data?.errors && typeof data.errors === 'object') {
      const firstFieldError = Object.values(data.errors).flat()[0];
      if (firstFieldError) return firstFieldError;
    }
    return data?.message || fallback;
  };

  const fetchProfiles = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { per_page: 15, page };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      const res = await operationApi.profiles.list(params);
      const d = res.data.data;
      setProfiles(d?.data || d || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filters.search, filters.status]);

  const loadMasterData = async () => {
    try {
      const [posRes, depRes] = await Promise.all([
        operationApi.positions.list({ per_page: 200 }),
        operationApi.departments.list({ per_page: 200 }),
      ]);
      const positions = posRes.data?.data?.data || posRes.data?.data || [];
      const departments = depRes.data?.data?.data || depRes.data?.data || [];
      setPositionsList(positions.map(p => p.name).filter(Boolean).sort());
      setDepartmentsList(departments.map(d => d.name).filter(Boolean).sort());
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchProfiles(1); }, [fetchProfiles]);

  useEffect(() => {
    loadMasterData();
  }, []);

  const uploadFile = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { mediaApi } = await import('../../services/api');
      const res = await mediaApi.upload(formData);
      return res.data?.url || res.data?.data?.url || null;
    } catch (err) { return null; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let photoUrl = editing?.photo_url || '';
      let ktpUrl = editing?.ktp_url || '';
      if (photoFile) photoUrl = await uploadFile(photoFile) || photoUrl;
      if (ktpFile) ktpUrl = await uploadFile(ktpFile) || ktpUrl;
      const payload = { ...form, photo_url: photoUrl, ktp_url: ktpUrl };
      if (editing) { const data = { ...payload }; if (!data.password) delete data.password; await operationApi.profiles.update(editing.id, data); }
      else { await operationApi.profiles.create(payload); }
      setShowForm(false); setEditing(null); setPhotoPreview(null); setKtpPreview(null); setPhotoFile(null); setKtpFile(null);
      resetForm(); fetchProfiles(pagination.current_page);
    } catch (err) {
      alert(getErrorMessage(err, 'Gagal menyimpan data pekerja'));
    } finally { setSaving(false); }
  };

  const resetForm = () => {
    setForm({ email: '', password: 'password123', employee_id: '', full_name: '', phone: '', address: '', date_of_birth: '', gender: '', position: '', department: '', hire_date: '', contract_end_date: '', emergency_contact: '', emergency_phone: '', blood_type: '', notes: '', status: 'active' });
  };

  const openNewForm = async () => {
    await loadMasterData();
    setEditing(null); resetForm(); setPhotoPreview(null); setKtpPreview(null); setPhotoFile(null); setKtpFile(null); setShowForm(true);
  };
  const handleEdit = async (profile) => {
    await loadMasterData();
    setEditing(profile);
    setForm({
      email: profile.user?.email || '', password: '', employee_id: profile.employee_id || '',
      full_name: profile.full_name || '', phone: profile.phone || '', address: profile.address || '',
      date_of_birth: profile.date_of_birth || '', gender: profile.gender || '',
      position: profile.position || '', department: profile.department || '', hire_date: profile.hire_date || '', contract_end_date: profile.contract_end_date || '',
      emergency_contact: profile.emergency_contact || '', emergency_phone: profile.emergency_phone || '',
      blood_type: profile.blood_type || '', notes: profile.notes || '', status: profile.status || 'active',
    });
    setPhotoPreview(profile.photo_url || null); setKtpPreview(profile.ktp_url || null); setPhotoFile(null); setKtpFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pekerja ini?')) return;
    try { await operationApi.profiles.delete(id); fetchProfiles(pagination.current_page); } catch (err) { alert('Gagal menghapus'); }
  };

  const openDetail = async (profile) => {
    setSelectedProfile(profile); setActiveTab('idcard'); setPricingData(null); setPricingLoading(true); setAssignmentLoading(true);
    try {
      const [priceRes, pkgRes, itemRes, assignRes] = await Promise.all([
        operationApi.profilePrices.get(profile.id).catch(() => ({ data: { data: { package_prices: [], item_prices: [] } } })),
        operationApi.packages().catch(() => ({ data: { data: [] } })),
        operationApi.serviceItems().catch(() => ({ data: { data: [] } })),
        operationApi.profileAssignments.getAssigned(profile.id).catch(() => ({ data: { data: { items: [], packages: [] } } })),
      ]);
      setPricingData(priceRes.data.data);
      setPackages(pkgRes.data.data?.data || pkgRes.data.data || []);
      setItems(itemRes.data.data?.data || itemRes.data.data || []);
      
      const assignedItems = assignRes.data.data?.items || [];
      const assignedPkgs = assignRes.data.data?.packages || [];
      setAssignedItemIds(assignedItems.map(i => i.id));
      setAssignedPackageIds(assignedPkgs.map(p => p.id));
      setSelectedItemIds(assignedItems.map(i => i.id));
      setSelectedPackageIds(assignedPkgs.map(p => p.id));
    } catch (err) { console.error(err); } finally { setPricingLoading(false); setAssignmentLoading(false); }
  };

  const saveAssignments = async () => {
    if (!selectedProfile) return;
    setAssignmentLoading(true);
    try {
      await Promise.all([
        operationApi.profileAssignments.assignItems(selectedProfile.id, { service_item_ids: selectedItemIds }),
        operationApi.profileAssignments.assignPackages(selectedProfile.id, { service_package_ids: selectedPackageIds }),
      ]);
      setAssignedItemIds(selectedItemIds);
      setAssignedPackageIds(selectedPackageIds);
      alert('Layanan berhasil diassign!');
    } catch (err) { alert('Gagal mengassign layanan'); } finally { setAssignmentLoading(false); }
  };

  const setDiscount = async (type, targetId, discountPercent, discountAmount, notes) => {
    if (!selectedProfile) return; setPricingLoading(true);
    try {
      if (type === 'package') {
        await operationApi.profilePrices.setPackagePrice(selectedProfile.id, { service_package_id: targetId, discount_percent: parseFloat(discountPercent) || 0, discount_amount: parseFloat(discountAmount) || 0, notes });
      } else {
        await operationApi.profilePrices.setItemPrice(selectedProfile.id, { service_item_id: targetId, discount_percent: parseFloat(discountPercent) || 0, discount_amount: parseFloat(discountAmount) || 0, notes });
      }
      const priceRes = await operationApi.profilePrices.get(selectedProfile.id); setPricingData(priceRes.data.data);
    } catch (err) { alert('Gagal set diskon'); } finally { setPricingLoading(false); }
  };

  const removeDiscount = async (type, priceId) => {
    if (!selectedProfile) return; setPricingLoading(true);
    try {
      if (type === 'package') await operationApi.profilePrices.deletePackagePrice(selectedProfile.id, priceId);
      else await operationApi.profilePrices.deleteItemPrice(selectedProfile.id, priceId);
      const priceRes = await operationApi.profilePrices.get(selectedProfile.id); setPricingData(priceRes.data.data);
    } catch (err) { alert('Gagal hapus diskon'); } finally { setPricingLoading(false); }
  };

  const copyReferralLink = () => {
    const code = selectedProfile?.card_code || '000000000000';
    navigator.clipboard.writeText(`${FRONTEND_URL}/order/${code}`).then(() => alert('Link referral disalin!')).catch(() => alert('Gagal menyalin'));
  };

  const downloadHDCard = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloadingCard(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        width: cardRef.current.offsetWidth * 3,
        height: cardRef.current.offsetHeight * 3,
        style: { transform: 'scale(1)', transformOrigin: 'top left' },
      });
      const link = document.createElement('a');
      link.download = `ID-Card-${selectedProfile?.full_name?.replace(/\s+/g, '-') || 'worker'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) { alert('Gagal download gambar'); console.error(err); }
    finally { setDownloadingCard(false); }
  }, [selectedProfile]);

  const printIDCard = () => {
    if (!selectedProfile) return;
    const referralLink = `${FRONTEND_URL}/order/${selectedProfile.card_code || '000000000000'}`;
    const win = window.open('', '_blank', 'width=500,height=750');
    const p = selectedProfile;
    const html = `<!DOCTYPE html><html><head><title>ID Card - ${p.full_name}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{display:flex;justify-content:center;align-items:center;min-height:100vh;background:#fff;font-family:Arial,sans-serif}
        .card{width:380px;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.12);margin:20px}
        .header{background:linear-gradient(135deg,#0B1F3A,#0d9488);color:#fff;padding:20px 24px 16px;text-align:center}
        .header h2{font-size:20px;font-weight:700;letter-spacing:1px}
        .header .subtitle{font-size:10px;opacity:0.8;margin-top:2px;text-transform:uppercase;letter-spacing:2px}
        .photo-area{display:flex;justify-content:center;padding:0;margin-top:0;position:relative;z-index:2}
        .photo-wrapper{width:96px;height:96px;border-radius:50%;border:4px solid #fff;background:#e2e8f0;object-fit:cover;box-shadow:0 4px 16px rgba(0,0,0,0.2);margin-top:-20px;overflow:hidden;background:#fff}
        .photo-wrapper img{width:100%;height:100%;object-fit:cover}
        .photo-placeholder{width:96px;height:96px;border-radius:50%;background:#0d9488;color:#fff;display:flex;align-items:center;justify-content:center;font-size:36px;font-weight:700}
        .info{padding:16px 24px 12px;text-align:center}
        .info .name{font-size:18px;font-weight:700;color:#1e293b;margin-bottom:2px}
        .info .role{font-size:11px;color:#64748b;margin-bottom:12px}
        .info .row{display:flex;justify-content:space-between;padding:6px 0;font-size:11px;color:#475569;border-bottom:1px solid #f1f5f9;text-align:left}
        .info .row .label{font-weight:600;color:#94a3b8;min-width:90px}
        .qr-area{padding:8px 24px 16px;text-align:center}
        .qr-area .code{font-family:'Courier New',monospace;font-size:13px;font-weight:700;color:#0d9488;letter-spacing:2px;margin-bottom:8px}
        .footer{background:#f8fafc;padding:10px 24px;text-align:center;font-size:9px;color:#94a3b8;border-top:1px solid #e2e8f0}
        @media print{body{background:#fff;margin:0}.card{box-shadow:none;border-radius:0;margin:0;page-break-after:avoid}}
        #qr-container{display:flex;justify-content:center;align-items:center;margin:0 auto;width:fit-content}
      </style>
      <script src="https://cdn.jsdelivr.net/npm/qrcode/build/qrcode.min.js"></script>
      </head><body>
      <div class="card">
        <div class="header"><h2>Klinnovac</h2><div class="subtitle">Operation Worker · ID Card</div></div>
        <div class="photo-area">
          <div class="photo-wrapper">${p.photo_url ? `<img src="${p.photo_url}" onerror="this.innerHTML='<div class=photo-placeholder>${(p.full_name||'?')[0]}</div>'"/>` : `<div class="photo-placeholder">${(p.full_name||'?')[0]}</div>`}</div>
        </div>
        <div class="info">
          <div class="name">${p.full_name||''}</div>
          <div class="role">${p.position||''}${p.department ? ' · '+p.department : ''}</div>
          <div class="row"><span class="label">NIP</span><span>${p.employee_id||'-'}</span></div>
          <div class="row"><span class="label">Jabatan</span><span>${p.position||'-'}</span></div>
          <div class="row"><span class="label">Telepon</span><span>${p.phone||'-'}</span></div>
          <div class="row"><span class="label">Masa Kontrak</span><span>${p.hire_date?new Date(p.hire_date).toLocaleDateString('id-ID',{day:'numeric',month:'short',year:'numeric'}):'-'}${p.contract_end_date?' — '+new Date(p.contract_end_date).toLocaleDateString('id-ID',{day:'numeric',month:'short',year:'numeric'}):''}</span></div>
        </div>
        <div class="qr-area">
          <div class="code">${p.card_code||'000000000000'}</div>
          <div id="qr-container"></div>
          <p style="font-size:8px;color:#94a3b8;margin-top:6px">Scan QR untuk order via referral</p>
        </div>
        <div class="footer">Klinnovac · ID Card Valid · ${p.card_code||''}</div>
      </div>
      <script>
        new QRCode(document.getElementById('qr-container'), {
          text: '${referralLink}',
          width: 100,
          height: 100,
          colorDark: '#0d9488',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.M
        });
        setTimeout(() => window.print(), 500);
      </script>
      </body></html>`;
    win.document.write(html);
    win.document.close();
  };

  const formatPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(p);
  const pages = [];
  for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) pages.push(i);

  return (
    <AdminLayout active="Pekerja Operasi">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-semibold text-gray-800 mb-1">Pekerja Operasi</h1>
          <button onClick={openNewForm} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark text-sm flex items-center gap-2"><UserPlus size={16} /> Tambah Pekerja</button>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Cari (Nama/NIP)</label><input placeholder="Cari..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Status</label><select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">Semua</option>{STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>
        </div>

        {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pekerja</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIP</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Posisi / Dept</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID Card</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th></tr></thead>
                <tbody className="divide-y divide-gray-200">
                  {profiles.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.photo_url ? <img src={p.photo_url} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-brand/30 flex-shrink-0" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} /> : null}
                          <div className="w-9 h-9 bg-brand/10 rounded-full items-center justify-center text-navy font-bold text-sm flex-shrink-0" style={{ display: p.photo_url ? 'none' : 'flex' }}>{p.full_name?.charAt(0)}</div>
                          <div><p className="text-sm font-medium text-gray-900">{p.full_name}</p><p className="text-xs text-gray-400">{p.user?.email}</p></div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{p.employee_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{p.position}{p.department ? <span className="text-gray-400"> · {p.department}</span> : ''}</td>
                      <td className="px-4 py-3"><span className="font-mono text-xs text-navy bg-brand/10 px-2 py-1 rounded">{p.card_code || '-'}</span></td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full font-medium ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.status}</span></td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button onClick={() => openDetail(p)} className="text-navy hover:text-brand-dark text-xs" title="ID Card"><IdCard size={14} /></button>
                        <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-800 text-xs"><Edit size={14} /></button>
                        <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-800 text-xs"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {profiles.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Belum ada pekerja</td></tr>}
                </tbody>
              </table>
            </div>
            {pagination.last_page > 1 && (<div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3"><span className="text-sm text-gray-500">Total {pagination.total} · Halaman {pagination.current_page} dari {pagination.last_page}</span><div className="flex items-center gap-1"><button disabled={pagination.current_page === 1} onClick={() => fetchProfiles(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">«</button>{pages.map(p => <button key={p} onClick={() => fetchProfiles(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300'}`}>{p}</button>)}<button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchProfiles(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">»</button></div></div>)}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b"><h2 className="text-lg font-bold text-gray-800">{editing ? 'Edit Pekerja' : 'Tambah Pekerja Baru'}</h2><button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Upload Foto Profil</label><input type="file" accept="image/*" onChange={e => { const f=e.target.files?.[0]; if(f){ if(f.size>MAX_FILE_SIZE)return alert('Ukuran file maks 15MB'); setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f)); } }} className="w-full text-xs" />{photoPreview&&<img src={photoPreview} alt="Preview" className="mt-2 w-24 h-24 object-cover rounded-lg border" />}</div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Upload KTP</label><input type="file" accept="image/*" onChange={e => { const f=e.target.files?.[0]; if(f){ if(f.size>MAX_FILE_SIZE)return alert('Ukuran file maks 15MB'); setKtpFile(f); setKtpPreview(URL.createObjectURL(f)); } }} className="w-full text-xs" />{ktpPreview&&<img src={ktpPreview} alt="KTP" className="mt-2 w-40 h-24 object-cover rounded-lg border" />}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Email *</label><input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">{editing?'Password (kosongkan jika tidak ganti)':'Password *'}</label><input type="password" required={!editing} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">NIP/NIK *</label><input type="text" required value={form.employee_id} onChange={e=>setForm({...form,employee_id:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Nama Lengkap *</label><input type="text" required value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Telepon</label><input type="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Jenis Kelamin</label><select value={form.gender} onChange={e=>setForm({...form,gender:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">- Pilih -</option>{GENDER_OPTIONS.map(g=><option key={g} value={g}>{g}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Tanggal Lahir</label><input type="date" value={form.date_of_birth} onChange={e=>setForm({...form,date_of_birth:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Golongan Darah</label><select value={form.blood_type} onChange={e=>setForm({...form,blood_type:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">- Pilih -</option>{BLOOD_OPTIONS.map(b=><option key={b} value={b}>{b}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Posisi</label><select value={form.position} onChange={e=>setForm({...form,position:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">- Pilih Posisi -</option>{positionsList.map(pos=><option key={pos} value={pos}>{pos}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Departemen</label><select value={form.department} onChange={e=>setForm({...form,department:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="">- Pilih Departemen -</option>{departmentsList.map(dep=><option key={dep} value={dep}>{dep}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Tanggal Masuk</label><input type="date" value={form.hire_date} onChange={e=>setForm({...form,hire_date:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Tanggal Habis Kontrak</label><input type="date" value={form.contract_end_date} onChange={e=>setForm({...form,contract_end_date:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Status</label><select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">{STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              <div className="border-t pt-4"><h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Heart size={14} /> Kontak Darurat</h3><div className="grid grid-cols-2 gap-4"><div><label className="block text-xs font-medium text-gray-600 mb-1">Nama Kontak Darurat</label><input type="text" value={form.emergency_contact} onChange={e=>setForm({...form,emergency_contact:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div><div><label className="block text-xs font-medium text-gray-600 mb-1">Telepon Darurat</label><input type="text" value={form.emergency_phone} onChange={e=>setForm({...form,emergency_phone:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div></div></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Alamat</label><textarea rows={2} value={form.address} onChange={e=>setForm({...form,address:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Catatan</label><textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div className="flex justify-end gap-3 pt-4 border-t"><button type="button" onClick={()=>{setShowForm(false);setEditing(null)}} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Batal</button><button type="submit" disabled={saving} className="px-4 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark disabled:opacity-50">{saving?'Menyimpan...':'Simpan'}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><IdCard size={20} /> {selectedProfile.full_name}</h2>
              <button onClick={() => setSelectedProfile(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-6">
              <div className="flex gap-4 mb-6 border-b overflow-x-auto">
                {[
                  { key: 'idcard', label: 'ID Card', icon: <QrCode size={14} /> },
                  { key: 'assignments', label: 'Assign Layanan', icon: <PackagePlus size={14} /> },
                  { key: 'discount', label: 'Diskon Khusus', icon: <Percent size={14} /> },
                  { key: 'referral', label: 'Link Referral', icon: <Link2 size={14} /> }
                ].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${activeTab===tab.key?'text-navy border-brand':'text-gray-400 border-transparent hover:text-gray-600'}`}>{tab.icon}{tab.label}</button>
                ))}
              </div>

              {activeTab === 'idcard' && (
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-md font-semibold text-gray-700 flex items-center gap-2"><QrCode size={16} /> Kartu Identitas</h3>
                    <div className="flex gap-2">
                      <button onClick={downloadHDCard} disabled={downloadingCard} className="px-3 py-1.5 bg-navy text-white rounded-lg text-xs hover:bg-navy-light flex items-center gap-1 disabled:opacity-50"><Download size={14} /> {downloadingCard ? 'Mendownload...' : 'Download HD'}</button>
                      <button onClick={printIDCard} className="px-3 py-1.5 bg-brand text-white rounded-lg text-xs hover:bg-brand-dark flex items-center gap-1"><Printer size={14} /> Cetak</button>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div ref={cardRef} className="bg-white rounded-xl border overflow-hidden shadow-lg" style={{ width: '400px', minHeight: '550px' }}>
                      {/* Header - no photo overlap */}
                      <div className="bg-gradient-to-r from-navy to-brand text-white p-6 text-center" style={{ background: 'linear-gradient(135deg, #0B1F3A, #0d9488)' }}>
                        <h3 className="font-bold text-xl tracking-wide">Klinnovac</h3>
                        <p className="text-xs opacity-80 mt-1 tracking-widest uppercase">Operation Worker · ID Card</p>
                      </div>

                      {/* Photo - contained properly */}
                      <div className="flex justify-center py-4 bg-white">
                        <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                          {selectedProfile.photo_url ? (
                            <img src={selectedProfile.photo_url} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;border-radius:50%;background:#0d9488;color:#fff;display:flex;align-items:center;justify-content:center;font-size:32px;font-weight:700">${selectedProfile.full_name?.charAt(0)||'?'}</div>`; }} />
                          ) : (
                            <div className="w-full h-full rounded-full bg-brand text-white flex items-center justify-center text-3xl font-bold">{selectedProfile.full_name?.charAt(0)}</div>
                          )}
                        </div>
                      </div>

                      {/* Info */}
                      <div className="px-6 pb-2">
                        <div className="text-center mb-3">
                          <h4 className="text-lg font-bold text-gray-800">{selectedProfile.full_name}</h4>
                          <p className="text-xs text-gray-500">{selectedProfile.position} · {selectedProfile.department}</p>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="font-semibold text-gray-400 w-24">NIP</span><span className="text-gray-700 font-mono">{selectedProfile.employee_id}</span></div>
                          <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="font-semibold text-gray-400 w-24">Jabatan</span><span className="text-gray-700">{selectedProfile.position || '-'}</span></div>
                          <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="font-semibold text-gray-400 w-24">Telepon</span><span className="text-gray-700">{selectedProfile.phone || '-'}</span></div>
                          <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="font-semibold text-gray-400 w-24">Masa Kontrak</span><span className="text-gray-700">{selectedProfile.hire_date ? new Date(selectedProfile.hire_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}{selectedProfile.contract_end_date ? ` — ${new Date(selectedProfile.contract_end_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</span></div>
                        </div>
                      </div>

                      {/* QR Code - real scannable */}
                      <div className="px-6 pb-5 text-center">
                        <p className="font-mono text-sm font-bold text-navy tracking-widest mb-3">{selectedProfile.card_code || '000000000000'}</p>
                        <div className="inline-flex items-center justify-center bg-white p-2 rounded-xl border-2 border-brand/20">
                          <QRCodeSVG
                            value={`${FRONTEND_URL}/order/${selectedProfile.card_code || '000000000000'}`}
                            size={110}
                            level="M"
                            fgColor="#0d9488"
                            bgColor="#ffffff"
                            includeMargin={false}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-2">Scan QR untuk order via referral</p>
                      </div>

                      <div className="bg-gray-50 px-5 py-3 text-center text-[10px] text-gray-400 border-t">Klinnovac · ID Card Valid · Generated by CMS</div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'assignments' && (
                <AssignmentTabContent
                  packages={packages}
                  items={items}
                  assignedItemIds={assignedItemIds}
                  assignedPackageIds={assignedPackageIds}
                  selectedItemIds={selectedItemIds}
                  selectedPackageIds={selectedPackageIds}
                  setSelectedItemIds={setSelectedItemIds}
                  setSelectedPackageIds={setSelectedPackageIds}
                  onSave={saveAssignments}
                  assignmentLoading={assignmentLoading}
                  formatPrice={formatPrice}
                />
              )}

              {activeTab === 'discount' && (
                <DiscountTabContent 
                  packages={packages.filter(p => assignedPackageIds.includes(p.id))}
                  items={items.filter(i => assignedItemIds.includes(i.id))}
                  pricingData={pricingData}
                  pricingLoading={pricingLoading}
                  onSave={setDiscount}
                  onDelete={removeDiscount}
                  formatPrice={formatPrice}
                />
              )}

              {activeTab === 'referral' && (
                <div>
                  <h3 className="text-md font-semibold text-gray-700 mb-4 flex items-center gap-2"><Link2 size={16} /> Link Referral Pekerja</h3>
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <div className="inline-flex items-center justify-center mb-4 bg-white p-3 rounded-xl border-2 border-brand/20">
                      <QRCodeSVG value={`${FRONTEND_URL}/order/${selectedProfile.card_code||'000000000000'}`} size={120} level="M" fgColor="#0d9488" bgColor="#ffffff" />
                    </div>
                    <p className="text-xs text-gray-500 mb-2">Kode Unik: <span className="font-mono font-bold text-navy">{selectedProfile.card_code||'-'}</span></p>
                    <div className="flex items-center gap-2 max-w-md mx-auto"><input readOnly value={`${FRONTEND_URL}/order/${selectedProfile.card_code||'000000000000'}`} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono bg-white" /><button onClick={copyReferralLink} className="px-3 py-2 bg-brand text-white rounded-lg text-xs hover:bg-brand-dark flex items-center gap-1"><Copy size={14}/>Salin</button></div>
                    <p className="text-[10px] text-gray-400 mt-3">Pelanggan yang order melalui link ini akan otomatis tercatat sebagai referral dari {selectedProfile.full_name}.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function AssignmentTabContent({ packages, items, assignedItemIds, assignedPackageIds, selectedItemIds, selectedPackageIds, setSelectedItemIds, setSelectedPackageIds, onSave, assignmentLoading, formatPrice }) {
  return (
    <div>
      <h3 className="text-md font-semibold text-gray-700 mb-4 flex items-center gap-2"><PackagePlus size={16} /> Assign Layanan & Paket</h3>
      <p className="text-xs text-gray-500 mb-6">Pilih layanan dan paket yang dapat ditawarkan oleh pekerja ini</p>

      {assignmentLoading ? (
        <p className="text-gray-400 text-sm py-4">Loading...</p>
      ) : (
        <div className="space-y-6">
          {/* Items Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-600 mb-4 flex items-center gap-1"><Tag size={14} /> Item Layanan ({selectedItemIds.length} dipilih)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {items.map(item => (
                <label key={`item-${item.id}`} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={selectedItemIds.includes(item.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItemIds([...selectedItemIds, item.id]);
                      } else {
                        setSelectedItemIds(selectedItemIds.filter(id => id !== item.id));
                      }
                    }}
                    className="w-4 h-4 text-brand rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">{formatPrice(item.price)}</p>
                  </div>
                  {assignedItemIds.includes(item.id) && <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded">Assigned</span>}
                </label>
              ))}
            </div>
          </div>

          {/* Packages Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-600 mb-4 flex items-center gap-1"><PackagePlus size={14} /> Paket ({selectedPackageIds.length} dipilih)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {packages.map(pkg => (
                <label key={`pkg-${pkg.id}`} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={selectedPackageIds.includes(pkg.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPackageIds([...selectedPackageIds, pkg.id]);
                      } else {
                        setSelectedPackageIds(selectedPackageIds.filter(id => id !== pkg.id));
                      }
                    }}
                    className="w-4 h-4 text-brand rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{pkg.name}</p>
                    <p className="text-xs text-gray-500">{formatPrice(pkg.base_price)}</p>
                  </div>
                  {assignedPackageIds.includes(pkg.id) && <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded">Assigned</span>}
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <button onClick={onSave} disabled={assignmentLoading || (selectedItemIds.length === 0 && selectedPackageIds.length === 0)} className="px-6 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark disabled:opacity-50 font-semibold">
              {assignmentLoading ? 'Menyimpan...' : 'Simpan Assignment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DiscountTabContent({ packages, items, pricingData, pricingLoading, onSave, onDelete, formatPrice }) {
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  return (
    <div>
      <h3 className="text-md font-semibold text-gray-700 mb-4 flex items-center gap-2"><Percent size={16} /> Diskon Khusus Per Pekerja</h3>
      
      {pricingLoading ? (
        <p className="text-gray-400 text-sm py-4">Loading...</p>
      ) : (
        <div className="space-y-6">
          {/* Paket Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-1"><PackagePlus size={14} /> Paket Layanan</h4>
            
            {/* Selection Checkboxes */}
            {packages.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 space-y-2">
                <p className="text-[10px] font-medium text-gray-500 mb-2">Pilih paket untuk diberi diskon:</p>
                {packages.map(pkg => {
                  const hasDiscount = (pricingData?.package_prices || []).find(pp => pp.service_package_id === pkg.id);
                  const isSelected = selectedPackages.includes(pkg.id);
                  return (
                    <label key={'sel-p-' + pkg.id} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={isSelected || !!hasDiscount} 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPackages([...selectedPackages, pkg.id]);
                          } else {
                            setSelectedPackages(selectedPackages.filter(id => id !== pkg.id));
                          }
                        }}
                        className="w-4 h-4 text-brand"
                      />
                      <span className="text-xs text-gray-700">{pkg.name} ({formatPrice(pkg.base_price)})</span>
                      {hasDiscount && <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Sudah</span>}
                    </label>
                  );
                })}
              </div>
            )}
            
            {/* Existing Discounts */}
            <div className="space-y-2">
              {packages.map(pkg => {
                const e = (pricingData?.package_prices || []).find(pp => pp.service_package_id === pkg.id);
                return (
                  <DiscountRow 
                    key={'p-' + pkg.id} 
                    name={pkg.name} 
                    basePrice={pkg.base_price} 
                    existing={e} 
                    type="package" 
                    targetId={pkg.id} 
                    onSave={onSave} 
                    onDelete={onDelete} 
                    formatPrice={formatPrice}
                    showOnly={!selectedPackages.includes(pkg.id) && !e}
                  />
                );
              })}
            </div>
          </div>

          {/* Item Layanan Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-1"><Tag size={14} /> Item Layanan</h4>
            
            {/* Selection Checkboxes */}
            {items.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 space-y-2">
                <p className="text-[10px] font-medium text-gray-500 mb-2">Pilih item untuk diberi diskon:</p>
                {items.map(item => {
                  const hasDiscount = (pricingData?.item_prices || []).find(ip => ip.service_item_id === item.id);
                  const isSelected = selectedItems.includes(item.id);
                  return (
                    <label key={'sel-i-' + item.id} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={isSelected || !!hasDiscount} 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems([...selectedItems, item.id]);
                          } else {
                            setSelectedItems(selectedItems.filter(id => id !== item.id));
                          }
                        }}
                        className="w-4 h-4 text-brand"
                      />
                      <span className="text-xs text-gray-700">{item.name} ({formatPrice(item.price)})</span>
                      {hasDiscount && <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Sudah</span>}
                    </label>
                  );
                })}
              </div>
            )}
            
            {/* Existing Discounts */}
            <div className="space-y-2">
              {items.map(item => {
                const e = (pricingData?.item_prices || []).find(ip => ip.service_item_id === item.id);
                return (
                  <DiscountRow 
                    key={'i-' + item.id} 
                    name={item.name} 
                    basePrice={item.price} 
                    existing={e} 
                    type="item" 
                    targetId={item.id} 
                    onSave={onSave} 
                    onDelete={onDelete} 
                    formatPrice={formatPrice}
                    showOnly={!selectedItems.includes(item.id) && !e}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiscountRow({ name, basePrice, existing, type, targetId, onSave, onDelete, formatPrice, showOnly = false }) {
  const [expanded, setExpanded] = useState(false);
  const [percent, setPercent] = useState(existing?.discount_percent||0);
  const [amount, setAmount] = useState(existing?.discount_amount||0);
  const [notes, setNotes] = useState(existing?.notes||'');
  const [saving, setSaving] = useState(false);

  useEffect(()=>{setPercent(existing?.discount_percent||0);setAmount(existing?.discount_amount||0);setNotes(existing?.notes||'');},[existing]);

  const handleSave = async () => { setSaving(true); await onSave(type, targetId, percent, amount, notes); setSaving(false); };
  
  let finalPrice = basePrice;
  if ((existing?.discount_percent||0) > 0) {
    finalPrice = basePrice * (1 - (existing?.discount_percent||0) / 100);
  } else if ((existing?.discount_amount||0) > 0) {
    finalPrice = Math.max(0, basePrice - (existing?.discount_amount||0));
  }
  
  // Auto-calculate Rp from percentage
  const handlePercentChange = (e) => {
    const percentVal = parseFloat(e.target.value) || 0;
    setPercent(percentVal);
    // Calculate equivalent Rp amount
    const calculatedAmount = Math.round(basePrice * (percentVal / 100));
    setAmount(calculatedAmount);
  };

  // Don't show if not selected and no existing discount
  if (showOnly) return null;

  return (
    <div className="border border-gray-200 rounded-lg p-3 text-xs">
      <div className="flex justify-between items-center">
        <div className="flex-1"><p className="font-medium text-gray-800">{name}</p><p className="text-gray-400">{formatPrice(basePrice)}</p></div>
        {existing ? (
          <div className="text-right flex items-center gap-2">
            <div><p className="text-navy font-bold">{formatPrice(finalPrice)}</p>{(existing.discount_percent>0||existing.discount_amount>0)&&<p className="text-[10px] text-navy">Diskon: {existing.discount_percent}% + {formatPrice(existing.discount_amount)}</p>}</div>
            <button onClick={()=>onDelete(type,existing.id)} className="text-red-400 hover:text-red-600"><X size={14}/></button>
            <button onClick={()=>setExpanded(!expanded)} className="text-blue-500 hover:text-blue-700 text-[10px]">Edit</button>
          </div>
        ) : <button onClick={()=>setExpanded(!expanded)} className="text-navy hover:text-brand-dark text-[11px] font-medium">+ Diskon</button>}
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-gray-400">Diskon % (auto-hitung Rp)</label>
              <input type="number" min="0" max="100" value={percent} onChange={handlePercentChange} className="w-full border border-gray-200 rounded px-2 py-1 text-xs"/>
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-gray-400">Diskon Rp (dari %)</label>
              <input type="number" min="0" value={amount} disabled className="w-full border border-gray-200 rounded px-2 py-1 text-xs bg-gray-50"/>
            </div>
          </div>
          <div><label className="text-[10px] text-gray-400">Catatan</label><input type="text" value={notes} onChange={e=>setNotes(e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1 text-xs"/></div>
          <div className="flex justify-end gap-2"><button onClick={()=>setExpanded(false)} className="text-gray-400 text-[10px]">Batal</button><button onClick={handleSave} disabled={saving} className="px-3 py-1 bg-brand text-white rounded text-[10px] hover:bg-brand-dark disabled:opacity-50">{saving?'...':'Simpan'}</button></div>
        </div>
      )}
    </div>
  );
}